import { UserRole, SelectOption } from '../types';

// User Roles
export const USER_ROLES: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  cluster_level_manager: 'Cluster Level Manager',
  store_manager: 'Store Manager',
  quality_manager: 'Quality Manager',
  process_expert: 'Process Expert',
  shift_in_charge: 'Shift In-Charge',
  associate: 'Associate',
  security_staff: 'Security Staff',
  mis: 'MIS',
};

// User Role Options for Select
export const USER_ROLE_OPTIONS: SelectOption[] = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'cluster_level_manager', label: 'Cluster Level Manager' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'quality_manager', label: 'Quality Manager' },
  { value: 'process_expert', label: 'Process Expert' },
  { value: 'shift_in_charge', label: 'Shift In-Charge' },
  { value: 'associate', label: 'Associate' },
  { value: 'security_staff', label: 'Security Staff' },
  { value: 'mis', label: 'MIS' },
];

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH_TOKEN: '/auth/refresh-token',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
    VERIFY: '/auth/verify',
  },
  
  // Users
  USERS: {
    LIST: '/users',
    CREATE: '/users',
    GET_BY_ID: (id: string) => `/users/${id}`,
    PROFILE: (id: string) => `/users/${id}/profile`,
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
    UPDATE_STATUS: (id: string) => `/users/${id}/status`,
    REGENERATE_PIN: (id: string) => `/users/${id}/regenerate-pin`,
    ATTENDANCE: (id: string) => `/users/${id}/attendance`,
    LEAVES: (id: string) => `/users/${id}/leaves`,
    DOCUMENTS: (id: string) => `/users/${id}/documents`,
    WORK_SCHEDULE: (id: string) => `/users/${id}/work-schedule`,
  },
  
  // Clusters
  CLUSTERS: {
    LIST: '/clusters',
    CREATE: '/clusters',
    GET_BY_ID: (id: string) => `/clusters/${id}`,
    UPDATE: (id: string) => `/clusters/${id}`,
    DELETE: (id: string) => `/clusters/${id}`,
  },
  
  // Stores
  STORES: {
    LIST: '/stores',
    CREATE: '/stores',
    GET_BY_ID: (id: string) => `/stores/${id}`,
    UPDATE: (id: string) => `/stores/${id}`,
    DELETE: (id: string) => `/stores/${id}`,
    UPDATE_ATTENDANCE_SETTINGS: (id: string) => `/stores/${id}/attendance-settings`,
    UPDATE_MANAGER: (id: string) => `/stores/${id}/manager`,
  },
  
  // Store Licenses
  STORE_LICENSES: {
    GET_BY_STORE: (storeId: string) => `/store-licenses/stores/${storeId}`,
    GET_BY_ID: (licenseId: string) => `/store-licenses/${licenseId}`,
    CREATE: '/store-licenses',
    UPDATE: (licenseId: string) => `/store-licenses/${licenseId}`,
    DELETE: (licenseId: string) => `/store-licenses/${licenseId}`,
    ADD_DOCUMENTS: (licenseId: string) => `/store-licenses/${licenseId}/documents`,
    DELETE_DOCUMENT: (documentId: string) => `/store-licenses/documents/${documentId}`,
  },
  
  // Shifts
  SHIFTS: {
    LIST: '/shifts',
    CREATE: '/shifts',
    GET_BY_ID: (id: string) => `/shifts/${id}`,
    UPDATE: (id: string) => `/shifts/${id}`,
    DELETE: (id: string) => `/shifts/${id}`,
  },
  
  // Attendance
  ATTENDANCE: {
    LIST: '/attendance',
    CREATE: '/attendance',
    GET_BY_ID: (id: string) => `/attendance/${id}`,
    UPDATE: (id: string) => `/attendance/${id}`,
    DELETE: (id: string) => `/attendance/${id}`,
    USER_ATTENDANCE: (userId: string) => `/attendance/user/${userId}`,
    STATS: '/attendance/stats',
    ANALYTICS: '/attendance/analytics',
    EXPORT: '/attendance/export',
  },

  // User Documents
  USER_DOCUMENTS: {
    LIST_BY_USER: (userId: string) => `/user-documents/users/${userId}`,
    CREATE_FOR_USER: (userId: string) => `/user-documents/users/${userId}`,
  },
  
  // Leaves
  LEAVES: {
    LIST: '/leaves',
    CREATE: '/leaves',
    GET_BY_ID: (id: string) => `/leaves/${id}`,
    UPDATE: (id: string) => `/leaves/${id}`,
    CANCEL: (id: string) => `/leaves/${id}/cancel`,
    APPROVE: (id: string) => `/leaves/${id}/approve`,
    BALANCE: '/leaves/balance/me',
    CALENDAR: '/leaves/calendar/me',
    PENDING: '/leaves/admin/pending',
    REPORTS: '/leaves/admin/reports',
    EXPORT: '/leaves/export',
    STATS: '/leaves/stats',
    USER_HISTORY: (userId: string) => `/leaves/user/${userId}/history`,
  },
  
  // Dashboard
  DASHBOARD: {
    DATA: '/dashboard',
    STORES: '/dashboard/stores',
    ATTENDANCE: '/dashboard/attendance',
    NOTIFICATIONS: '/dashboard/notifications',
  },
  
  // Performers
  PERFORMERS: {
    WEEK: '/performers/week',
    LIST: '/performers',
    CREATE: '/performers',
    GET_BY_ID: (id: string) => `/performers/${id}`,
    UPDATE: (id: string) => `/performers/${id}`,
    DELETE: (id: string) => `/performers/${id}`,
  },
  
  // Settings
  SETTINGS: {
    COMPANY: '/settings/company',
    ATTENDANCE_MODES: '/settings/attendance-modes',
    SECURITY: '/settings/security',
    NOTIFICATIONS: '/settings/notifications',
    BACKUP: '/settings/backup',
    SYSTEM_INFO: '/settings/system-info',
    STORAGE_CLEANUP: '/settings/storage-cleanup',
    STORAGE_CLEANUP_RUN: '/settings/storage-cleanup/run',
  },
  
  // Meetings
  MEETINGS: {
    LIST: '/meetings',
    CREATE: '/meetings',
    GET_BY_ID: (id: string) => `/meetings/${id}`,
    UPDATE: (id: string) => `/meetings/${id}`,
    DELETE: (id: string) => `/meetings/${id}`,
    JOIN: (id: string) => `/meetings/${id}/join`,
    LEAVE: (id: string) => `/meetings/${id}/leave`,
    START: (id: string) => `/meetings/${id}/start`,
    END: (id: string) => `/meetings/${id}/end`,
    PERMISSIONS: '/meetings/permissions',
    UPDATE_PERMISSIONS: '/meetings/permissions',
    TOKEN: (id: string) => `/meetings/${id}/token`,
  },
  
  // Vehicle Captures
  VEHICLE_CAPTURES: {
    LIST: '/vehicle-captures',
    GET_BY_ID: (id: string) => `/vehicle-captures/${id}`,
    CREATE: '/vehicle-captures',
    DELETE: (id: string) => `/vehicle-captures/${id}`,
  },
  
  // Temperature Captures
  TEMPERATURE_CAPTURES: {
    LIST: '/temperature-captures',
    GET_BY_ID: (id: string) => `/temperature-captures/${id}`,
    CREATE: '/temperature-captures',
    UPDATE: (id: string) => `/temperature-captures/${id}`,
    DELETE: (id: string) => `/temperature-captures/${id}`,
    GET_BY_DATE: '/temperature-captures/by-date',
    TIME_SLOTS: '/temperature-captures/time-slots',
  },
  
  // Vendors
  VENDORS: {
    LIST: '/vendors',
    CREATE: '/vendors',
    GET_BY_ID: (id: string) => `/vendors/${id}`,
    UPDATE: (id: string) => `/vendors/${id}`,
    DELETE: (id: string) => `/vendors/${id}`,
    USERS: (id: string) => `/vendors/${id}/users`,
  },

  // Breaks
  BREAKS: {
    LIST: '/breaks',
    GET_BY_ATTENDANCE: (attendanceId: string) => `/breaks/attendance/${attendanceId}`,
    GET_BY_DATE: '/breaks',
    GET_ACTIVE: '/breaks/active',
  },
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  AUTH_USER: 'auth_user',
  REFRESH_TOKEN: 'refresh_token',
};

// Routes
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  USERS: '/users',
  WORK_TIMINGS: '/work-timings',
  CLUSTERS: '/clusters',
  STORES: '/stores',
  STORE_MANAGER_MANAGEMENT: '/store-manager-management',
  SHIFTS: '/shifts',
  ATTENDANCE: '/attendance',
  LEAVES: '/leaves',
  VISITORS: '/visitors',
  MEETINGS: '/meetings',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  VEHICLE_CAPTURES: '/vehicle-captures',
  TEMPERATURE_CAPTURES: '/temperature-captures',
  PERFORMERS: '/performers',
  ANNOUNCEMENTS: '/announcements',
  CONTACTS: '/contacts',
  VENDORS: '/vendors',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMIT_OPTIONS: [10, 25, 50, 100],
};

// Validation Rules
export const VALIDATION = {
  PHONE: {
    PATTERN: /^[6-9]\d{9,14}$/,
    MESSAGE: 'Please enter a valid phone number (10-15 digits starting with 6-9)',
  },
  PIN: {
    PATTERN: /^\d{4,6}$/,
    MESSAGE: 'PIN must be 4-6 digits',
  },
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MESSAGE: 'Please enter a valid email address',
  },
};

// Leave Types
export const LEAVE_TYPES = {
  SICK_LEAVE: 'sick_leave',
  CASUAL_LEAVE: 'casual_leave',
  EARNED_LEAVE: 'earned_leave',
  MATERNITY_LEAVE: 'maternity_leave',
  PATERNITY_LEAVE: 'paternity_leave',
  EMERGENCY_LEAVE: 'emergency_leave',
};

// Leave Status
export const LEAVE_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
};

// Leave Type Options for Select
export const LEAVE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'casual_leave', label: 'Casual Leave' },
  { value: 'earned_leave', label: 'Earned Leave' },
  { value: 'maternity_leave', label: 'Maternity Leave' },
  { value: 'paternity_leave', label: 'Paternity Leave' },
  { value: 'emergency_leave', label: 'Emergency Leave' },
];

// Category Levels
export const CATEGORY_LEVELS = {
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
};

// Category Level Options for Select
export const CATEGORY_LEVEL_OPTIONS: SelectOption[] = [
  { value: 'L1', label: 'L1' },
  { value: 'L2', label: 'L2' },
  { value: 'L3', label: 'L3' },
];

// Employee Types
export const EMPLOYEE_TYPES = {
  PERMANENT: 'permanent',
  CONTRACT: 'contract',
  TEMPORARY: 'temporary',
  INTERN: 'intern',
};

// Employee Type Options for Select
export const EMPLOYEE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'intern', label: 'Intern' },
];

// Gender Options
export const GENDER_OPTIONS: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

// Shift Types
export const SHIFT_TYPES = {
  GENERAL: 'general',
  DAY: 'day',
  NIGHT: 'night',
  FIRST: 'first',
  SECOND: 'second',
  TIME_BASED: 'time_based',
};

// Shift Type Options for Select
export const SHIFT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'general', label: 'General' },
  { value: 'day', label: 'Day Shift' },
  { value: 'night', label: 'Night Shift' },
  { value: 'first', label: 'First Shift' },
  { value: 'second', label: 'Second Shift' },
  { value: 'time_based', label: 'Time Based' },
];
